/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { MenuDisplayOption } from "common/constants/misc"
import { ServiceHours } from "types/api"
import { currency } from "order-manager/types/models/order_common"
import { AttributeStatus, ItemAttributeValue } from "./item_attribute"

interface ExceedChangeLimit {
  date: string
  limit: string
  range: string
}

export interface DisabledEditModifierGroup extends ExceedChangeLimit {
  modifierGroupID: string
}

export interface DisabledEditItem extends ExceedChangeLimit {
  itemID: string
}
export interface Weight {
  unit: string
  value: number
  count: number
}

export enum RestockSettingFreq {
  Hourly = "Hourly",
  Daily = "Daily",
  Weekly = "Weekly",
}

export interface RestockSetting {
  freq: RestockSettingFreq
  localtime: string
  byDay: string[] | null
  nextRestockTime: string
  maxStock: number
}

export interface ItemStock {
  enableIms: boolean
  currentStock: number
  enableRestock: boolean
  restockSetting: RestockSetting | null
}

export interface Pricing {
  priceInMin: number
  priceDisplay: string
}

export type AdvancedPricing = Record<string, Pricing>

export type Purchasability = Record<string, boolean>

export type Translation = Record<string, string>

export interface Item {
  itemID?: string
  itemName?: string
  categoryName?: string
  availableAt?: string
  categoryID?: string
  sectionName?: string
  sectionID?: string
  availableStatus?: number
  priceDisplay?: string
  priceRange?: string
  sortOrder?: number
  description?: string
  imageURL?: string
  priceInMin?: number
  itemCode?: string
  itemClassID?: string
  specialItemType?: string
  itemClassName?: string
  skuID?: string
  brandName?: string
  weight?: Weight | null
  itemStock?: ItemStock | null
  departmentName?: string
  departmentID?: string
  subDepartmentName?: string
  subDepartmentID?: string
  linkedModifierGroupIDs?: string[] | null
  soldByWeight?: boolean
  sellingTimeID?: string
  advancedPricing?: AdvancedPricing
  purchasability?: Purchasability
  attributes?: ItemAttributeValue[]
  imageURLs?: string[]
  parentItemClassID?: string
  parentItemClassName?: string
  descriptionTranslation?: TranslationSetting
  resetDescriptionTranslation?: TranslationSetting
  isDescriptionTranslating?: boolean
  nameTranslation?: TranslationSetting
  resetNameTranslation?: TranslationSetting
  isNameTranslating?: boolean
  translationError?: Map<string, TranslationFailedResponse>
  supportedAttributeClusterIDs?: string[]
  webPURL?: string
  webPURLs?: string[]
  aiGeneratedFields?: string[]
}

export interface TranslationFailedResponse {
  target: string
  language?: string
  message?: string
}

export interface TranslationDetail {
  nameTranslation?: TranslationSetting
  descriptionTranslation?: TranslationSetting
}

export interface TranslationSetting {
  translation: Translation
  originalText: string | undefined
  originalTranslationFromDS?: Translation
}
export interface BaseDepartment {
  itemClassID: string
  itemClassName: string
  sortOrder?: number
  totalItems?: number
  totalUnavailableItems?: number
  sectionName?: string
  sectionID?: string
  sellingTimeID?: string
}

export interface ItemClass {
  ItemClassID: string
  Name: string
  Status: string
  ParentID: string
  ParentClassName: string
}

export interface SubDepartment extends BaseDepartment {
  departmentName?: string
  departmentID?: string
  items: Item[]
  attributeCluster?: string[]
  itemWithoutPhotoCount?: number
}

export interface Department extends BaseDepartment {
  subDepartments: SubDepartment[]
}

export interface Category {
  categoryID?: string
  categoryName?: string
  sectionName?: string
  sectionID?: string
  sellingTimeID?: string
  availableStatus?: number
  sortOrder?: number
  items?: Item[]
  isNameTranslating?: boolean
  nameTranslation?: TranslationSetting
  resetNameTranslation?: TranslationSetting
  translationError?: Map<string, TranslationFailedResponse>
}
export interface Range {
  start: string
  end: string
}

export interface OpeningHour {
  ranges: Range[]
}

export interface SellingSchedule {
  id?: string
  name?: string
  serviceHours?: OpeningHour[]
  startPeriod?: string
  endPeriod?: string
}

export interface GroupedServiceHour {
  dayRange: string
  periods: string[]
}

export interface SellingTimeMetaData {
  sellingTimeID: string
  name: string
  serviceHours: ServiceHours
  startTime: string
  endTime: string
  status: string
  updatedAt: string
  createdAt: string
}

export interface SellingTime extends SellingTimeMetaData {
  groupedServiceHours: GroupedServiceHour[]
}

export interface Section {
  sectionID?: string
  sectionName: string
  sortOrder?: number
  availableStatus?: number
  categories?: Category[]
  departments?: Department[]
  openingHours?: OpeningHour[]
}

export interface Modifier {
  modifierID: string
  modifierName: string
  priceDisplay: string
  priceRange?: string
  isNeedExtraCost: boolean
  availableStatus: number
  sortOrder: number
  quantity: number
  priceInMin: number
  maxModifierSelectionQuantity?: number
  advancedPricing?: AdvancedPricing
  nameTranslation?: TranslationSetting
  resetNameTranslation?: TranslationSetting
  isNameTranslating?: boolean
  translationError?: Map<string, TranslationFailedResponse>
}

export interface ModifierGroup {
  modifierGroupID: string
  modifierGroupName: string
  selectionRangeMin: number
  selectionRangeMax: number
  maxModifierSelectionQuantity?: number
  isFlexibleQuantityEnabled?: boolean
  modifiers: Modifier[]
  relatedItemIDs: string[]
  isNameTranslating?: boolean
  translationError?: Map<string, TranslationFailedResponse>
  nameTranslation?: TranslationSetting
  resetNameTranslation?: TranslationSetting
}

export interface PreDefinedAttributeMetaData {
  attributesLimitPerItem: number
}

export interface FreeTextAttributeMetaData {
  maxLength: number
}

export interface ValueData {
  value: number
}

export interface DecimalAttributeMetaData {
  max: ValueData
  min: ValueData
  numberOfDecimalPlaces: ValueData
  unitLocalization: string
  units: string
}

export interface AttributeMetaData {
  preDefinedMetadata?: PreDefinedAttributeMetaData
  freeTextMetadata?: FreeTextAttributeMetaData
  decimalMetadata?: DecimalAttributeMetaData
}

export interface AttributeClusterOption {
  isMandatory?: boolean
  isLoading?: boolean
  attributeClusterOptionID: string
  countryID: string
  isMexSelfServe: boolean
  isShownToPax?: boolean
}

export interface AttributeOption {
  attributeClusterID: string
  businessType: string
  cluster: string
  countryID: string
  id: string
  name: string
  nameLocalization: string
  status: AttributeStatus
}

export interface AttributeCluster {
  attributeClusterOption: AttributeClusterOption
  attributeClusterOptions?: AttributeOption[]
  attributeClusterOptionsHasMore?: boolean
  attributeClusterType: string
  businessType: string
  metadata: AttributeMetaData
  nameLocalization: string
  status?: AttributeStatus
  name: string
  icon?: string
  id: string
}

export interface AttributesData {
  attributeClusters?: { [s: string]: AttributeCluster }
  isLoading?: boolean
  isLoadingSupportedAttributeCluster?: boolean
}

export interface Menu {
  sections?: Section[]
  sellingTimes?: SellingTime[]
  categories?: Category[]
  departments?: Department[]
  modifierGroups: ModifierGroup[]
  isMenuEditorEnabled: boolean
  attributesData?: AttributesData
  modifierGroupSelected?: ModifierGroup
  modifierSelected?: Modifier[]
  isAlcoholSaleAllowed?: boolean
  isTobaccoSaleAllowed?: boolean
}

export enum BusinessType {
  Food = 0,
  Shop = 1,
}

export interface SpecialItemSetting {
  specialItemType: string
  editableFields: string[]
  enableMerchantMenuEditor?: boolean
  enablePromo?: boolean
}

export interface MultiPicsSetting {
  enable: boolean
  maxSize: number
}

export interface GrabMartFeatureEnable {
  isGrabMartV2ForItemAttributesEnabled: boolean
  isGrabMartV2ForEditingItemAttributesEnabled: boolean
  isGrabMartV2Enabled: boolean
  isGrabMartV2ForAppBrandEnabled: boolean
  isPrintSKUCodeEnabled: boolean
  departmentEnabled: boolean
}

export interface MerchantFlag {
  specialItemSettings?: SpecialItemSetting[]
  multiPicsSetting?: MultiPicsSetting
  grabMartFeatureEnabled?: GrabMartFeatureEnable
  isAlcoholSaleAllowed?: boolean
  isTobaccoSaleAllowed?: boolean
  isDepartmentTaggingEnhancement?: boolean
  isInventoryManagementEnabled?: boolean
}

export interface LanguageFullName {
  en?: string
  id?: string
  km?: string
  ms?: string
  my?: string
  th?: string
  vi?: string
  zh?: string
}

export interface MenuTranslationSetting {
  menuDefaultLanguage: string
  menuSupportedLanguages: Array<string>
  isAutoTranslationEnabled?: boolean
  isTranslationFieldMandatory: boolean
  languageFullNames: LanguageFullName
  translationFieldMandatoryLanguages?: Array<string>
  translationModelSupportedLanguages?: Array<string>
}

// alias to prevent too many re-typings - this is retrieved by fetchStoreDetail
export type OutletStore = Merchant
export type OutletStoreV2 = MerchantV2

// food/food-trx/pb/common.proto
export interface Merchant {
  merchantID: string
  merchantName: string
  city: string
  modelType: string
  status: string
  chainMerchant?: string
  currency?: string
  timezone?: string
  revenueInMin?: number
  netEarningsInMin?: number
  payableOrderCounts?: number
  backgroundImg?: string
  featureFlag?: number
  businessType?: BusinessType
  address?: string
  merchantFlag?: MerchantFlag
  supportDelivery?: boolean
  supportTakeaway?: boolean
  supportDineIn?: boolean
  blockOnDemandOrders?: boolean
  isScheduledOrderEnabled?: boolean
  storefrontEnabled?: boolean
  isInventoryManagementEnabled?: boolean
  isAlcoholSaleAllowed?: boolean
  // unused
  currentBalanceInMin?: number
  adjustments?: number
  grabPayGrabID?: string
  merchantGroupID?: string
  menuGroupV2ID?: string
  merchantGroupName?: string
  advertiseFeeInMin?: number
  specialMerchantType?: string
  isIntegratedPartner?: boolean
  useSpecialTooltip?: boolean
  menuTranslationSetting?: MenuTranslationSetting
}

export interface MerchantV2 {
  ID: string
  name: string
  backgroundImg?: string
  blockOnDemandOrders?: boolean
  businessType?: number
  rating?: number
  contractNumber?: string
  latitude?: number
  longitude?: number
  openingHours?: OpeningHour[]
  countryID?: number
  cityID?: number
  timezone?: string
  address?: string
  tax?: string
  status?: string
  paymentType?: number
  isOpen?: boolean
  model?: string
  cePhone?: string
  countryCode?: string
  currency?: currency
  isDineInOpen?: boolean
  isMenuEditingEnabled?: boolean
  isPromoModuleEnabled?: boolean
  isPasscodeSkipped?: boolean
  isPasscodeEnabled?: boolean
  isInboxEnabled?: boolean
  isOrderLevelDiscountEnabled?: boolean
  isScheduledOrderEnabled?: boolean
  isEnableTroy?: boolean
  grabPayGrabID?: string
  merchantFlag?: MerchantFlag
  role?: string
  featureFlag?: number
  supportDelivery?: boolean
  menuTranslationSetting?: MenuTranslationSetting
  specialMerchantType?: string
  storefrontEnabled?: boolean
  supportDineIn?: boolean
  supportTakeaway?: boolean
  supportScheduledOrders?: boolean
}

export enum DeliverOption {
  ONLY_DELIVERY = "ONLY_DELIVERY",
  ONLY_TAKEAWAY = "ONLY_TAKEAWAY",
  ONLY_DINEIN = "ONLY_DINEIN",
  DELIVERY_TAKEAWAY = "DELIVERY_TAKEAWAY",
  DELIVERY_DINEIN = "DELIVERY_DINEIN",
  TAKEAWAY_DINEIN = "TAKEAWAY_DINEIN",
  DELIVERY_TAKEAWAY_DINEIN = "DELIVERY_TAKEAWAY_DINEIN",
}

// food/food-trx/pb/foodtroy.proto
export interface MerchantStoreInfo {
  merchantID: string
  merchantName: string
  city: string
  address?: string
  modelType: string
  status: string
  deliverOption?: DeliverOption
  businessType?: BusinessType
  menuDisplayOption?: MenuDisplayOption
  timezone?: string
  itemsWithoutPhotosCount?: number
}

export interface MenuGroup {
  menuGroupID: string
  name: string
  ownerType: 0 | 1 | null // MerchantGroup=0 or Brand=1
  ownerID: string
  countryID: number | null
  status: string
  businessType: number | null
  menuType: 0 | 1 | null // menu structure: 0 - category based,  1: department based
  timeZone: string
  latestPropagateStatus: number | null
  createdAt: string
  updatedAt: string
  itemsWithoutPhotosCount?: number
  languageSettings?: MenuTranslationSetting
}

export type MenuGroupLinkedStore = {
  label: string
  value: string
}

export type MerchantType = OutletStore | OutletStoreV2

export type SuggestedDepartment = {
  departmentID: string
  departmentName: string
  departmentConfidence: number
  subDepartmentID: string
  subDepartmentName: string
  subDepartmentConfidence: number
}

export type GrabCategory = {
  itemClassID: string
  itemClassName: string
  parentClassID: string
  parentClassName?: string
}

export type PostPredictionFeedbacksGrabCategory = {
  predictionID: string
  suggestedDepartments: SuggestedDepartment[]
  selectedDepartmentID: string
  selectedSubDepartmentID: string
  itemID: string
}

type MenuTranslationTextType = "name" | "description"

type MenuTranslationEntity = "category" | "item" | "modifierGroup" | "modifier"

export type MenuTranslationRequestData = {
  textType: MenuTranslationTextType
  entity: MenuTranslationEntity
  text: string
  textSourceLang: string
  textTargetLang: Array<string>
  destinationKey?: string
}

export type VerifyTranslationRequestData = {
  entityType: string
  entityID?: string
  nameTranslation?: TranslationSetting
  descriptionTranslation?: TranslationSetting
}

export type StoreFilter = {
  search: string
  city: string[]
  asc?: boolean
}

export type MerchantCity = {
  cityID: number
  cityName: string
  storeCount: number
}

export type MerchantBusinessType = {
  businessType: number
  storeCount: number
}

export type MerchantDeliverOption = {
  deliverOption: string
  storeCount: number
}

export enum BusinessTypeCode {
  Food = 0,
  Mart = 1,
}
